import { computed, inject, Injectable, signal } from '@angular/core';
import { SOFTLINE_AI_AGENT_CONTEXT_SOURCES } from '../ai-agent.tokens';
import { AgUiContext } from '../types/ag-ui.types';
import { AiAgentContextSource } from '../types/context-source.type';

export interface AiAgentContextChip {
  id: string;
  label: string;
  entry: AgUiContext;
}

/**
 * Collects what the visible application state reports to the agent. Sources are
 * read per turn; removing a chip excludes that source from the NEXT turn only,
 * so the user never has to re-enable it afterwards.
 */
@Injectable({ providedIn: 'root' })
export class AiAgentContextService {
  private readonly sources: readonly AiAgentContextSource[] =
    inject(SOFTLINE_AI_AGENT_CONTEXT_SOURCES, { optional: true }) ?? [];

  private readonly excluded = signal<ReadonlySet<string>>(new Set());

  private readonly available = computed<AiAgentContextChip[]>(() =>
    this.sources
      .map((s) => ({ id: s.id, label: s.label(), entry: s.entry() }))
      .filter((c): c is AiAgentContextChip => c.entry !== null)
  );

  /** Chips to render above the composer — excluded sources are already gone. */
  readonly chips = computed(() => {
    const excluded = this.excluded();
    return this.available().filter((c) => !excluded.has(c.id));
  });

  exclude(id: string): void {
    this.excluded.update((s) => new Set(s).add(id));
  }

  /** Everything currently reported, ignoring exclusions (`get_current_context`). */
  all(): AgUiContext[] {
    return this.available().map((c) => c.entry);
  }

  /** The entries for the turn being sent; clears the one-turn exclusions. */
  forTurn(): AgUiContext[] {
    const entries = this.chips().map((c) => c.entry);
    if (this.excluded().size > 0) this.excluded.set(new Set());
    return entries;
  }
}
